import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { 
  AccountBook, 
  AccountBookSummary, 
  AccountBookCreateData, 
  AccountBookUpdateData,
  Category,
  LoadingState 
} from '@/types';
import { firestoreService } from '@/services/firestoreService';

// 진행 중인 대시보드 요청 키 (`userId-accountNumber-year`) — 동시 중복 호출 방지
const inFlightDashboardFetches = new Set<string>();

// sharedBooks 데이터 존재 여부 확인 함수
const checkSharedBooksData = async (userId: string): Promise<boolean> => {
  try {
    const { getSharedStores } = await import('@/services/firestoreService');
    const stores = await getSharedStores(userId);
    return stores.length > 0;
  } catch (error) {
    console.error('❌ sharedBooks 데이터 확인 실패:', error);
    return false;
  }
};

interface AccountBookStore extends LoadingState {
  // 상태
  accountBooks: AccountBook[];
  currentAccountBook: AccountBook | null;
  currentAccountNumber: number;
  /** 캐시 소유 계정 — 다른 계정 로그인 시 이전 캐시가 비치지 않게 함 */
  ownerUid: string | null;
  summaries: AccountBookSummary[];
  categories: Category[];
  userId: string | null; // 현재 사용자 ID
  isInitialized: boolean; // 초기화 완료 여부
  isPremium: boolean; // 프리미엄 사용자 여부
  
  // Dashboard 데이터 상태
  dashboardData: {
    kpiData: {
      totalIncome: number;
      totalExpense: number;
      netIncome: number;
      recordCount: number;
      lastUpdated: Date | null;
    };
    previousYearKpiData: {
      totalIncome: number;
      totalExpense: number;
      netIncome: number;
      recordCount: number;
    };
    monthlyData: {
      month: number;
      income: number;
      expense: number;
      netIncome: number;
    }[];
    categoryExpenses: {
      category: string;
      amount: number;
      percentage: number;
    }[];
    revenueSources: {
      category: string;
      amount: number;
      percentage: number;
    }[];
    selectedYear: number;
  };
  
  // 액션들
  setUserId: (userId: string) => void;
  setCurrentAccountBook: (accountNumber: number) => void;
  fetchAccountBooks: () => Promise<void>;
  fetchAccountBook: (accountBookId: number) => Promise<void>;
  createAccountBook: (data: AccountBookCreateData) => Promise<void>;
  updateAccountBook: (accountBookId: string, data: AccountBookUpdateData) => Promise<void>;
  deleteAccountBook: (accountBookId: string) => Promise<void>;
  fetchCategories: (accountBookId: number) => Promise<void>;
  createCategory: (category: Omit<Category, 'id'>) => Promise<void>;
  updateCategory: (categoryId: string, updates: Partial<Category>) => Promise<void>;
  deleteCategory: (categoryId: string) => Promise<void>;
  
  // Dashboard 데이터 액션들
  fetchDashboardData: (year: number) => Promise<void>;
  setSelectedYear: (year: number) => void;
  
  // 유틸리티 메서드
  getAccountBookByNumber: (accountNumber: number) => AccountBook | undefined;
  getAccountBookById: (accountBookId: number) => AccountBook | undefined;
  getSummaryByNumber: (accountNumber: number) => AccountBookSummary | undefined;
  getCategoriesByType: (type: 'income' | 'expense') => Category[];
  clearCurrentAccountBook: () => void;
}

export const useAccountBookStore = create<AccountBookStore>()(
  devtools(
    persist(
      (set, get) => ({
      // 초기 상태
      isLoading: false,
      error: null,
      accountBooks: [],
      currentAccountBook: null,
      currentAccountNumber: 1, // 기본값: 1호점
      ownerUid: null,
      summaries: [],
      categories: [],
      userId: null,
      isInitialized: false,
      isPremium: false,
      
      // Dashboard 데이터 초기 상태
      dashboardData: {
        kpiData: {
          totalIncome: 0,
          totalExpense: 0,
          netIncome: 0,
          recordCount: 0,
          lastUpdated: null,
        },
        previousYearKpiData: {
          totalIncome: 0,
          totalExpense: 0,
          netIncome: 0,
          recordCount: 0,
        },
        monthlyData: [],
        categoryExpenses: [],
        revenueSources: [],
        selectedYear: new Date().getFullYear(), // 기본값을 현재 년도로 설정
      },

      // 사용자 ID 설정
      setUserId: async (userId: string) => {
        const currentUserId = get().userId;
        if (currentUserId === userId) {
          return;
        }
        
        // 다른 계정으로 로그인하면 이전 계정의 캐시 데이터를 비운다
        if (userId && get().ownerUid !== userId) {
          set({
            ownerUid: userId,
            accountBooks: [],
            currentAccountBook: null,
            summaries: [],
            dashboardData: {
              kpiData: { totalIncome: 0, totalExpense: 0, netIncome: 0, recordCount: 0, lastUpdated: null },
              previousYearKpiData: { totalIncome: 0, totalExpense: 0, netIncome: 0, recordCount: 0 },
              monthlyData: [],
              categoryExpenses: [],
              revenueSources: [],
              selectedYear: new Date().getFullYear(),
            },
          });
        }

        set({ userId, isInitialized: false, isPremium: false });

        if (userId) {
          try {
            // 구독 상태는 AuthGuard에서 관리하므로 여기서는 sharedBooks 데이터만 확인
            const hasSharedBooksData = await checkSharedBooksData(userId);
            
            
            set({ isPremium: hasSharedBooksData });
            
            // 사용자 ID가 설정되면 가계부 목록 가져오기
            get().fetchAccountBooks();
          } catch (error) {
            console.error('❌ 프리미엄 상태 확인 실패:', error);
            
            // 에러 발생 시 sharedBooks 데이터 확인
            try {
              const hasSharedBooksData = await checkSharedBooksData(userId);
              console.error('📊 에러 발생 시 sharedBooks 데이터 확인:', hasSharedBooksData);
              set({ isPremium: hasSharedBooksData });
            } catch (sharedBooksError) {
              console.error('❌ sharedBooks 데이터 확인도 실패:', sharedBooksError);
              set({ isPremium: false });
            }
            
            // 에러가 발생해도 가계부 목록은 가져오기
            get().fetchAccountBooks();
          }
        } else {
          // userId가 빈 문자열이면 초기화
          set({ 
            accountBooks: [],
            currentAccountBook: null,
            summaries: [],
            categories: [],
            isPremium: false,
            isInitialized: true
          });
        }
      },

      // 현재 가계부 설정
      setCurrentAccountBook: (accountNumber: number) => {
        const currentNumber = get().currentAccountNumber;
        if (currentNumber === accountNumber) return; // 이미 선택된 가계부면 무시


        // Employee Store 캐시 초기화 (순환 참조 방지를 위해 동적 import)
        import('./employeeStore').then(({ useEmployeeStore }) => {
          useEmployeeStore.getState().clearCache();
        });

        // Dashboard 데이터 초기화 (가계부 변경 시 데이터 혼란 방지)
        set({ 
          currentAccountNumber: accountNumber,
          currentAccountBook: null, // 즉시 UI 반응을 위해 초기화
          dashboardData: {
            kpiData: {
              totalIncome: 0,
              totalExpense: 0,
              netIncome: 0,
              recordCount: 0,
              lastUpdated: null,
            },
            previousYearKpiData: {
              totalIncome: 0,
              totalExpense: 0,
              netIncome: 0,
              recordCount: 0,
            },
            monthlyData: [],
            categoryExpenses: [],
            revenueSources: [],
            selectedYear: get().dashboardData.selectedYear, // 년도는 유지
          },
        });

        // 새 가계부 정보 가져오기
        const accountBook = get().getAccountBookByNumber(accountNumber);
        if (accountBook) {
          set({ currentAccountBook: accountBook });
          // 카테고리 정보도 함께 가져오기
          get().fetchCategories(accountBook.id);
        } else {
          // 가계부 정보가 없어도 가상의 accountBook 생성 (데이터 조회를 위해)
          // accountNumber만으로도 데이터를 조회할 수 있도록 함
          const virtualAccountBook = {
            id: accountNumber,
            name: `${accountNumber}호점`,
            businessName: undefined,
            description: undefined,
            ownerId: get().userId || '',
            accountNumber: accountNumber,
            isActive: true,
            createdAt: new Date(),
            updatedAt: new Date(),
            settings: {
              timezone: 'Asia/Seoul',
              fiscalYearStart: new Date('2024-01-01'),
              taxRate: 10,
              categories: [],
            },
          };
          set({ currentAccountBook: virtualAccountBook });
        }
        
        // Dashboard 데이터 재로딩 (accountBook이 있든 없든 accountNumber로 데이터 조회)
        const selectedYear = get().dashboardData.selectedYear;
        get().fetchDashboardData(selectedYear);
      },

      // 가계부 목록 가져오기
      fetchAccountBooks: async () => {
        const { userId, isLoading, isPremium } = get();
        if (!userId) {
          return;
        }

        if (isLoading) {
          return;
        }

        set({ isLoading: true, error: null });
        try {
          
          let storeDocuments: any[] = [];
          
          // 프리미엄 사용자는 sharedBooks에서, 일반 사용자는 users에서 가져오기
          if (isPremium) {
            const { getSharedStores } = await import('@/services/firestoreService');
            storeDocuments = await getSharedStores(userId);
          } else {
            storeDocuments = await firestoreService.getUserStores(userId);
          }
          
          
          // StoreToAccountBookConverter를 AccountBook으로 변환 (검증 강화)
          const accountBooks: AccountBook[] = storeDocuments.map(store => {
            // accountNumber 검증 (필수 필드)
            if (store.accountNumber === undefined || store.accountNumber === null) {
              console.warn(`⚠️ accountBookStore: 매장(id: ${store.id})에 accountNumber가 없습니다. 기본값 1을 사용합니다.`);
            }
            
            const accountNumber = store.accountNumber ?? 1;
            
            // id와 accountNumber 일치성 검증
            if (store.id > 0 && store.id !== accountNumber) {
              console.warn(`⚠️ accountBookStore: 매장 ID(${store.id})와 accountNumber(${accountNumber})가 일치하지 않습니다. 문서에 따르면 일치해야 합니다.`);
            }
            
            // accountNumber 유효성 검증 (1-5)
            if (accountNumber < 1 || accountNumber > 5) {
              console.warn(`⚠️ accountBookStore: accountNumber(${accountNumber})가 유효한 범위(1-5)를 벗어났습니다.`);
            }
            
            return {
              id: store.id,
              name: store.name || `${accountNumber}호점`,
              businessName: store.businessName,
              description: store.description || '',
              ownerId: store.ownerId || '',
              accountNumber: accountNumber,
              isActive: store.isActive !== false,
              createdAt: store.createdAt?.toDate() || new Date(),
              updatedAt: store.updatedAt?.toDate() || new Date(),
              settings: {
                timezone: store.settings?.timezone || 'Asia/Seoul',
                fiscalYearStart: store.settings?.fiscalYearStart?.toDate() || new Date('2024-01-01'),
                taxRate: store.settings?.taxRate || 10,
                categories: [],
              },
            };
          });

          // 중복 제거 (accountNumber 기준)
          const uniqueAccountBooks = accountBooks.filter((book, index, self) => 
            index === self.findIndex(b => b.accountNumber === book.accountNumber)
          );
          
          set({ accountBooks: uniqueAccountBooks });

          // 요약 정보 생성 (임시로 계산)
          const summaries: AccountBookSummary[] = uniqueAccountBooks.map(accountBook => ({
            id: accountBook.id,
            name: accountBook.name,
            accountNumber: accountBook.accountNumber,
            totalIncome: 0, // TODO: 실제 데이터에서 계산
            totalExpense: 0, // TODO: 실제 데이터에서 계산
            netIncome: 0, // TODO: 실제 데이터에서 계산
            recordCount: 0, // TODO: 실제 데이터에서 계산
            lastUpdated: new Date(),
          }));

          set({ 
            summaries,
            isLoading: false,
          });

            // 현재 선택된 가계부 정보 설정 (기본값: 1호점)
            const currentNumber = get().currentAccountNumber;
            let currentAccountBook = uniqueAccountBooks.find(ab => ab.accountNumber === currentNumber);
            
            // 현재 가계부가 없으면 1호점으로 설정
            if (!currentAccountBook && uniqueAccountBooks.length > 0) {
              currentAccountBook = uniqueAccountBooks.find(ab => ab.accountNumber === 1);
              if (currentAccountBook) {
                set({ currentAccountNumber: 1 });
              }
            }
            
            if (currentAccountBook) {
              set({ currentAccountBook });
              get().fetchCategories(currentAccountBook.id);
              
              // Dashboard 데이터도 함께 로딩
              const selectedYear = get().dashboardData.selectedYear;
              get().fetchDashboardData(selectedYear);
            }

          // 초기화 완료 표시
          set({ isInitialized: true });
        } catch (error: any) {
          console.error('가계부 목록 가져오기 실패:', error);
          set({
            error: error.message || '가계부 목록을 가져오는데 실패했습니다.',
            isLoading: false,
          });
        }
      },

      // 특정 가계부 정보 가져오기
      fetchAccountBook: async (accountBookId: number) => {
        set({ isLoading: true, error: null });
        try {
          // TODO: Firebase Firestore에서 특정 가계부 정보 가져오기
          const accountBook = get().getAccountBookById(accountBookId);
          if (accountBook) {
            set({ currentAccountBook: accountBook });
            get().fetchCategories(accountBookId);
          }
          set({ isLoading: false });
        } catch (error: any) {
          set({
            error: error.message || '가계부 정보를 가져오는데 실패했습니다.',
            isLoading: false,
          });
        }
      },

      // 새 가계부 생성
      createAccountBook: async (_data: AccountBookCreateData) => {
        set({ isLoading: true, error: null });
        try {
          // TODO: Firebase Firestore에 새 가계부 생성
          throw new Error('가계부 생성은 아직 구현되지 않았습니다.');
        } catch (error: any) {
          set({
            error: error.message || '가계부 생성에 실패했습니다.',
            isLoading: false,
          });
        }
      },

      // 가계부 수정
      updateAccountBook: async (_accountBookId: string, _data: AccountBookUpdateData) => {
        set({ isLoading: true, error: null });
        try {
          // TODO: Firebase Firestore에서 가계부 정보 수정
          throw new Error('가계부 수정은 아직 구현되지 않았습니다.');
        } catch (error: any) {
          set({
            error: error.message || '가계부 수정에 실패했습니다.',
            isLoading: false,
          });
        }
      },

      // 가계부 삭제
      deleteAccountBook: async (_accountBookId: string) => {
        set({ isLoading: true, error: null });
        try {
          // TODO: Firebase Firestore에서 가계부 삭제
          throw new Error('가계부 삭제는 아직 구현되지 않았습니다.');
        } catch (error: any) {
          set({
            error: error.message || '가계부 삭제에 실패했습니다.',
            isLoading: false,
          });
        }
      },

      // 카테고리 목록 가져오기
      fetchCategories: async (_accountBookId: number) => {
        try {
          // TODO: Firebase Firestore에서 카테고리 목록 가져오기
          set({ categories: [] });
        } catch (error: any) {
          console.error('카테고리 목록을 가져오는데 실패했습니다:', error);
        }
      },

      // 새 카테고리 생성
      createCategory: async (_category: Omit<Category, 'id'>) => {
        set({ isLoading: true, error: null });
        try {
          // TODO: Firebase Firestore에 새 카테고리 생성
          throw new Error('카테고리 생성은 아직 구현되지 않았습니다.');
        } catch (error: any) {
          set({
            error: error.message || '카테고리 생성에 실패했습니다.',
            isLoading: false,
          });
        }
      },

      // 카테고리 수정
      updateCategory: async (_categoryId: string, _updates: Partial<Category>) => {
        set({ isLoading: true, error: null });
        try {
          // TODO: Firebase Firestore에서 카테고리 정보 수정
          throw new Error('카테고리 수정은 아직 구현되지 않았습니다.');
        } catch (error: any) {
          set({
            error: error.message || '카테고리 수정에 실패했습니다.',
            isLoading: false,
          });
        }
      },

      // 카테고리 삭제
      deleteCategory: async (_categoryId: string) => {
        set({ isLoading: true, error: null });
        try {
          // TODO: Firebase Firestore에서 카테고리 삭제
          throw new Error('카테고리 삭제는 아직 구현되지 않았습니다.');
        } catch (error: any) {
          set({
            error: error.message || '카테고리 삭제에 실패했습니다.',
            isLoading: false,
          });
        }
      },

      // 유틸리티 메서드들
      getAccountBookByNumber: (accountNumber: number) => {
        return get().accountBooks.find(ab => ab.accountNumber === accountNumber);
      },

      getAccountBookById: (accountBookId: number) => {
        return get().accountBooks.find(ab => ab.id === accountBookId);
      },

      getSummaryByNumber: (accountNumber: number) => {
        return get().summaries.find(summary => summary.accountNumber === accountNumber);
      },

      getCategoriesByType: (type: 'income' | 'expense') => {
        return get().categories.filter(cat => cat.type === type && cat.isActive);
      },

      clearCurrentAccountBook: () => {
        set({ currentAccountBook: null });
      },


      // Dashboard 데이터 가져오기
      fetchDashboardData: async (year: number) => {
        const { userId, currentAccountBook, currentAccountNumber } = get();
        if (!userId) {
          return;
        }

        // currentAccountBook이 없어도 currentAccountNumber를 사용하여 데이터 조회
        const accountNumber = currentAccountBook?.accountNumber ?? currentAccountNumber;
        if (!accountNumber) {
          return;
        }

        // 같은 요청이 이미 진행 중이면 무시 — 새로고침 시 여러 곳에서 동시 발화해도 1번만 나간다
        const fetchKey = `${userId}-${accountNumber}-${year}`;
        if (inFlightDashboardFetches.has(fetchKey)) {
          return;
        }
        inFlightDashboardFetches.add(fetchKey);

        // 기존 데이터는 유지한 채 백그라운드 갱신 (stale-while-revalidate)
        set({ error: null });

        try {

          // 통합 데이터 로딩 함수 사용 (프리미엄 사용자 자동 감지)
          const { loadDashboardData } = await import('@/services');

          // 현재 년도와 전년도 데이터를 병렬로 가져오기
          const [currentYearResult, previousYearResult] = await Promise.all([
            loadDashboardData(userId, year, accountNumber),
            loadDashboardData(userId, year - 1, accountNumber)
          ]);

          // 응답 대기 중 계정·연도가 바뀌었으면 폐기 — 늦게 온 stale 응답이 화면을 덮지 않게
          const latest = get();
          const latestAccountNumber =
            latest.currentAccountBook?.accountNumber ?? latest.currentAccountNumber;
          if (
            latest.userId !== userId ||
            latestAccountNumber !== accountNumber ||
            latest.dashboardData.selectedYear !== year
          ) {
            return;
          }

          set({
            dashboardData: {
              kpiData: currentYearResult.kpiData,
              previousYearKpiData: previousYearResult.kpiData,
              monthlyData: currentYearResult.monthlyData,
              categoryExpenses: currentYearResult.categoryExpenses,
              revenueSources: currentYearResult.revenueSources,
              selectedYear: latest.dashboardData.selectedYear,  // 기존 값 유지
            },
            isPremium: currentYearResult.isPremium,
          });

        } catch (error: any) {
          console.error('Dashboard 데이터 로드 실패:', error);
          set({
            error: error.message || 'Dashboard 데이터를 가져오는데 실패했습니다.',
          });
        } finally {
          inFlightDashboardFetches.delete(fetchKey);
        }
      },

      // 선택된 년도 설정
      setSelectedYear: (year: number) => {
        const currentYear = get().dashboardData.selectedYear;
        if (currentYear === year) {
          return;
        }
        
        
        // 연도 변경 시 이전 연도 데이터 초기화 + 즉시 selectedYear 업데이트 (데이터 혼란 방지)
        set({
          dashboardData: {
            kpiData: {
              totalIncome: 0,
              totalExpense: 0,
              netIncome: 0,
              recordCount: 0,
              lastUpdated: null,
            },
            previousYearKpiData: {
              totalIncome: 0,
              totalExpense: 0,
              netIncome: 0,
              recordCount: 0,
            },
            monthlyData: [],
            categoryExpenses: [],
            revenueSources: [],
            selectedYear: year,
          },
        });
        
        // 년도가 변경되면 새로운 데이터 가져오기 (비동기로 처리)
        setTimeout(() => {
          get().fetchDashboardData(year);
        }, 100); // 짧은 지연으로 UI 안정성 확보
      },
    }),
      // ── 표시용 캐시 persist ─────────────────────────────
      // 새로고침 시 마지막 화면 데이터를 즉시 보여주기 위한 로컬 캐시.
      // 실제 데이터는 초기화 흐름이 그대로 다시 불러와 갱신한다 (stale-while-revalidate).
      // isInitialized/userId/isLoading 은 절대 persist 하지 않는다 (초기화 로직 보존).
      {
        name: 'ilgijang.accountBookCache',
        storage: createJSONStorage(() => localStorage),
        partialize: (s) => ({
          ownerUid: s.ownerUid,
          accountBooks: s.accountBooks,
          currentAccountBook: s.currentAccountBook,
          currentAccountNumber: s.currentAccountNumber,
          isPremium: s.isPremium,
          dashboardData: {
            ...s.dashboardData,
            // Date 는 JSON 복원 시 string 이 되므로 제외 (KPIGrid 가 toLocaleDateString 호출)
            kpiData: { ...s.dashboardData.kpiData, lastUpdated: null },
          },
        }),
      }
    ),
    {
      name: 'account-book-store',
    }
  )
);
